// /guide: how to use cmx, from a one-command quickstart to the full CLI reference. Public, so
// it can be linked from install output and the CLI's help; signed in, it sits in the
// dashboard's chrome. Sections are anchors (#quickstart, #cli, …) with a contents list.

import { ShellCode } from '../ui/ShellCode';
import { IconArrowRight, IconBrandOpenai, IconBrandVscode, IconCheck, IconCopy, IconDownload, IconCommand, IconCompass, IconDeviceDesktop, IconDeviceMobile, IconFiles, IconInfoCircle, IconLayoutDashboard, IconLifebuoy, IconLink, IconLogout2, IconPlugConnected, IconRocket, IconShieldLock, IconTerminal, IconTerminal2, IconUser } from '@tabler/icons-react';
import { Children, useEffect, useState, type ReactNode } from 'react';
import { installCommand } from '../api';
import { Link } from '../router';
import { useStore } from '../store';
import { BranchedMenu } from '../ui/BranchedMenu';
import { GuideApps } from '../ui/GetApps';
import { IconBrandClaude } from '../ui/BrandIcons';
import { isMac, modLabel, numModLabel } from '../ui/keys';
import { CLI_ENV, CLI_GROUPS, ESCAPE_KEYS } from './guide/cliRef';
import { agentPrompt } from './GetStarted';
import { Brand, DashLayout } from './Shell';
import './guide.css';

const SECTIONS: { id: string; title: string; admin?: boolean }[] = [
  { id: 'quickstart', title: 'Quickstart' },
  { id: 'download', title: 'Apps and downloads' },
  { id: 'start', title: 'Get started' },
  { id: 'sessions', title: 'Sessions' },
  { id: 'workspace', title: 'Workspace' },
  { id: 'files', title: 'Files and clipboard' },
  { id: 'links', title: 'Links and previews' },
  { id: 'desktop', title: 'Desktop' },
  { id: 'phone', title: 'Phone and tablet' },
  { id: 'cli', title: 'Command line' },
  { id: 'apps', title: 'Editors and AI apps' },
  { id: 'account', title: 'Account and security' },
  { id: 'admin', title: 'Admin', admin: true },
  { id: 'troubleshooting', title: 'Troubleshooting' },
];

const SECTION_ICONS = {
  quickstart: IconRocket,
  start: IconCompass,
  sessions: IconTerminal2,
  workspace: IconLayoutDashboard,
  files: IconFiles,
  links: IconLink,
  desktop: IconDeviceDesktop,
  phone: IconDeviceMobile,
  download: IconDownload,
  cli: IconTerminal,
  apps: IconPlugConnected,
  account: IconUser,
  admin: IconShieldLock,
  troubleshooting: IconLifebuoy,
} as const;

const QS_TABS = [
  { id: 'install', label: 'Install', hint: 'Run in terminal', icon: IconDownload },
  { id: 'start', label: 'Set up', hint: 'Run in terminal', icon: IconTerminal2 },
  { id: 'connect', label: 'Connect apps', hint: 'Optional', icon: IconPlugConnected },
] as const;

/** The quickstart as a setup card: header, one tab per command, the body, and a well footer. */
function Quickstart({ install }: { install: string }) {
  const [tab, setTab] = useState<(typeof QS_TABS)[number]['id']>('install');
  const move = (d: number) => {
    const i = QS_TABS.findIndex((t) => t.id === tab);
    const next = QS_TABS[(i + d + QS_TABS.length) % QS_TABS.length].id;
    setTab(next);
    requestAnimationFrame(() => document.getElementById(`qs-tab-${next}`)?.focus());
  };
  return (
    <div className="qs">
      <div className="qs-head">
        <div>
          <h3 className="qs-title">Set up cmx</h3>
          <p className="qs-sub">Install cmx on this computer (macOS or Linux), then set up your cloud machine. Two commands.</p>
        </div>
        <a className="qs-link" href="/app">
          Open the workspace
          <IconArrowRight size={16} stroke={1.5} aria-hidden />
        </a>
        {/* the landing's + as ASCII, in the empty corner under the link */}
        <pre className="qs-ascii" aria-hidden="true">{'+ · + · + · + · + · +\n· + · + · + · + · + ·\n+ · + · + · + · + · +\n· + · + · + · + · + ·'}</pre>
      </div>
      <div
        className="qs-tabs"
        role="tablist"
        aria-label="Quickstart steps"
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') (e.preventDefault(), move(1));
          if (e.key === 'ArrowLeft') (e.preventDefault(), move(-1));
        }}
      >
        {QS_TABS.map((t) => (
          <button
            key={t.id}
            id={`qs-tab-${t.id}`}
            role="tab"
            type="button"
            className="qs-tab"
            aria-selected={tab === t.id}
            aria-controls={`qs-panel-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
          >
            <span className="qs-sw">
              <t.icon size={24} stroke={1.5} aria-hidden />
              <b>{t.label}</b>
              <span>{t.hint}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="qs-body" role="tabpanel" id="qs-panel-install" aria-labelledby="qs-tab-install" hidden={tab !== 'install'}>
        <div className="qs-group">
          <p>Run this in your terminal. It installs cmx, then opens a browser page where you approve the sign-in.</p>
          <Cmd testid="guide-install">{install}</Cmd>
        </div>
        <p className="qs-next">
          Next: the <b>Set up</b> tab.
        </p>
      </div>
      <div className="qs-body" role="tabpanel" id="qs-panel-start" aria-labelledby="qs-tab-start" hidden={tab !== 'start'}>
        <div className="qs-group">
          <p>Run this in your terminal.</p>
          <Cmd note="Starts your cloud machine, copies your tools, shell, coding agents, sign-ins and projects to it, then opens a session. In a project folder, it opens that project.">
            cmx
          </Cmd>
        </div>
        <Callout title="If setup can’t finish everything">
          <p>The rest goes on a to-do list. Paste this into a coding agent on your cloud machine to finish it:</p>
          <div className="prompt-row">
            <span className="prompt-text">
              {agentPrompt()
                .split(/(https?:\/\/\S+?)(?=\s)/)
                .map((part, i) => (/^https?:/.test(part) ? <code key={i}>{part}</code> : part))}
            </span>
            <Copy text={agentPrompt()} />
          </div>
        </Callout>
      </div>
      <div className="qs-body" role="tabpanel" id="qs-panel-connect" aria-labelledby="qs-tab-connect" hidden={tab !== 'connect'}>
        <div className="qs-group">
          <p>Optional. Lets the ChatGPT, Claude and VS Code apps run their agents on your cloud machine.</p>
          <Cmd>cmx connect</Cmd>
        </div>
      </div>
      <div className="qs-foot">
        <b>
          <IconLogout2 size={16} stroke={1.5} aria-hidden />
          Leave a session
        </b>
        <p>
          Press <K>Enter</K>, then <K>~</K>, then <K>.</K>. The session keeps running, and <C>cmx</C> brings you back. Prefer the browser? <a href="/app">Open the workspace</a> to see the same sessions, live in both.
        </p>
      </div>
    </div>
  );
}

function Copy({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-sm btn-ghost"
      onClick={() =>
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      {copied ? <IconCheck size={16} stroke={1.5} aria-hidden /> : <IconCopy size={16} stroke={1.5} aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/** An info banner: a tinted block with an icon, for a note that sits beside the steps. */
function Callout({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <aside className="callout" role="note">
      <IconInfoCircle size={20} stroke={1.5} aria-hidden />
      <div className="callout-body">
        {title && <b className="callout-title">{title}</b>}
        {children}
      </div>
    </aside>
  );
}

/** A command to type, with a Copy button. */
function Cmd({ children, note, testid }: { children: string; note?: string; testid?: string }) {
  const row = (
    <div className="guide-cmd" data-testid={testid}>
      <code>
        <span className="guide-prompt" aria-hidden="true">
          ${' '}
        </span>
        <ShellCode>{children}</ShellCode>
      </code>
      <Copy text={children} />
    </div>
  );
  // the note describes the command, so it sits under the box, not inside what gets copied
  return note ? (
    <div className="guide-cmd-group">
      {row}
      <p className="guide-cmd-note">{note}</p>
    </div>
  ) : (
    row
  );
}

function Section({ id, title, lead, children }: { id: string; title: string; lead: ReactNode; children?: ReactNode }) {
  return (
    <section className="guide-section" id={id} aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`}>
        <a href={`#${id}`}>{title}</a>
      </h2>
      <p className="guide-lead">{lead}</p>
      {children}
    </section>
  );
}

function Table({ head, rows }: { head: [string, string]; rows: [ReactNode, ReactNode][] }) {
  return (
    <div className="guide-table-wrap">
      <table className="guide-table">
        <thead>
          <tr>
            <th scope="col">{head[0]}</th>
            <th scope="col">{head[1]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([a, b], i) => (
            <tr key={i}>
              <td>{a}</td>
              <td>{b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AppName = ({ icon: Icon, children }: { icon: typeof IconCheck | typeof IconBrandClaude; children: ReactNode }) => (
  <strong className="app-name">
    <Icon size={16} stroke={2} aria-hidden="true" />
    {children}
  </strong>
);
/** A key cap. The ⌘ key is drawn as the Tabler command icon: the text glyph is small and uneven in the mono face. */
const K = ({ children }: { children: ReactNode }) => (
  <kbd>
    {Children.map(children, (c) =>
      typeof c === 'string'
        ? c.split(/(⌘)/).map((part, i) => (part === '⌘' ? <IconCommand key={i} className="kbd-icon" size={12} stroke={1.75} aria-label="Command" /> : part))
        : c,
    )}
  </kbd>
);
const C = ({ children }: { children: ReactNode }) => <code>{children}</code>;

// The contents rail groups the sections into three branches.
const BRANCHES: { label: string; ids: string[] }[] = [
  { label: 'Start', ids: ['quickstart', 'download', 'start'] },
  { label: 'Using cmx', ids: ['sessions', 'workspace', 'files', 'links', 'desktop', 'phone'] },
  { label: 'Reference', ids: ['cli', 'apps', 'account', 'admin', 'troubleshooting'] },
];

function GuideBody({ isAdmin }: { isAdmin: boolean }) {
  const install = installCommand();
  const mod = modLabel();
  const num = numModLabel();
  const sections = SECTIONS.filter((s) => !s.admin || isAdmin);
  const [active, setActive] = useState(sections[0].id);

  // Highlight the section being read in the contents list.
  useEffect(() => {
    const els = sections.map((s) => document.getElementById(s.id)).filter((e): e is HTMLElement => !!e);
    const io = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (seen[0]) setActive(seen[0].target.id);
      },
      { rootMargin: '0px 0px -70% 0px' },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [isAdmin]);

  // Deep links (#cli) land on their section, instantly rather than with the site's smooth
  // scroll. Web fonts and the signed-in header arrive after the first render and push the
  // section down, so for a moment it's re-aligned whenever the page's size changes, until the
  // reader scrolls themselves. A link followed on the open guide (the contents, a pasted #apps)
  // scrolls smoothly, then lands the same way: a scroll cut short still gets there.
  useEffect(() => {
    const yours = ['wheel', 'touchstart', 'keydown', 'pointerdown']; // the reader scrolling themselves
    let stop = () => {};
    const land = () => {
      stop();
      const id = location.hash.slice(1);
      if (!id) return;
      const go = () => document.getElementById(id)?.scrollIntoView({ behavior: 'instant', block: 'start' });
      go();
      const ro = new ResizeObserver(() => go());
      ro.observe(document.body);
      const t = setTimeout(() => stop(), 1500);
      const quit = () => stop();
      for (const ev of yours) addEventListener(ev, quit, { passive: true });
      stop = () => {
        ro.disconnect();
        clearTimeout(t);
        for (const ev of yours) removeEventListener(ev, quit);
        stop = () => {};
      };
    };
    let settle = () => {};
    const followed = () => {
      settle();
      const done = () => {
        settle();
        land();
      };
      const t = setTimeout(done, 1500); // no scrollend: it was there already
      const quit = () => settle();
      addEventListener('scrollend', done, { once: true });
      for (const ev of yours) addEventListener(ev, quit, { passive: true });
      settle = () => {
        clearTimeout(t);
        removeEventListener('scrollend', done);
        for (const ev of yours) removeEventListener(ev, quit);
        settle = () => {};
      };
    };
    land();
    addEventListener('hashchange', followed);
    return () => {
      removeEventListener('hashchange', followed);
      settle();
      stop();
    };
  }, []);

  return (
    <div className="guide" data-testid="guide">
      <nav className="guide-toc" aria-label="Guide contents" data-testid="guide-toc">
        <details open={matchMedia('(min-width: 961px)').matches}>
          <summary>On this page</summary>
          <BranchedMenu
            label="Guide sections"
            active={active}
            onSelect={setActive}
            sections={BRANCHES.map((b) => ({
              label: b.label,
              children: b.ids
                .map((id) => sections.find((s) => s.id === id))
                .filter((s): s is (typeof sections)[number] => !!s)
                .map((s) => ({ value: s.id, label: s.title, href: `#${s.id}`, icon: SECTION_ICONS[s.id as keyof typeof SECTION_ICONS] })),
            }))}
          />
        </details>
      </nav>

      <div className="guide-content">
        <section className="guide-section guide-quick" id="quickstart" aria-labelledby="quickstart-h" data-testid="guide-quickstart">
          <h2 id="quickstart-h">
            <a href="#quickstart">Quickstart</a>
          </h2>
          <Quickstart install={install} />
        </section>

        <Section
          id="download"
          title="Apps and downloads"
          lead="Cloud Machine for Windows and Mac, cmx on your phone, and the cmx command line. Sign in to each with the same account: they all show the same sessions, live."
        >
          <GuideApps />
        </Section>

        <Section id="start" title="Get started" lead="Your cloud machine runs Linux and stays on. You reach it from the browser, your terminal, your editor and your AI apps, all at once.">
          <ol className="guide-steps">
            <li>
              <strong>Get a cloud machine.</strong> <Link to="/signup">Sign up</Link>, pick a size and pay: your cloud machine is ready a couple of minutes later. The{' '}
              <Link to="/dashboard">dashboard</Link> shows its size and region.
            </li>
            <li>
              <strong>Bring this computer’s setup.</strong> Run <C>cmx</C> in your terminal (the quickstart above): it moves your tools, shell, coding agents’ settings,
              sign-ins and the projects you pick, with your uncommitted work, to the same paths on your cloud machine. Secrets move only if you pick them.
            </li>
            <li>
              <strong>Open the workspace</strong> (<a href="/app">Open workspace</a>): terminal sessions, previews of your dev servers and the cloud machine’s desktop, in one tab.
            </li>
            <li>
              <strong>Use your own terminal</strong> with the <a href="#cli">cmx CLI</a> (the quickstart above), and your <a href="#apps">editor or AI app</a> with{' '}
              <C>cmx connect</C>.
            </li>
          </ol>
          <Callout>
            <p>Everything you start keeps running when you close the tab, shut the lid or lose Wi-Fi. Come back from any device and pick up where it is.</p>
          </Callout>
        </Section>

        <Section id="sessions" title="Sessions" lead="A session is a terminal running on your cloud machine. Open it in as many tabs, terminals and devices as you like; they all see the same screen.">
          <ul>
            <li>
              <strong>New:</strong> <em>New session</em> in the sidebar, the command palette, or <C>cmx new</C>. <strong>Rename:</strong> double-click the name (or ✎).{' '}
              <strong>End:</strong> the ✕ next to it, <C>cmx kill</C>, or <C>exit</C> in the shell.
            </li>
            <li>
              <strong>Order:</strong> what needs you first, then what's running, then what finished while you were away (<em>Done</em>), then the rest, the latest
              first. The list holds still while your pointer is on it.
            </li>
            <li>
              <strong>Nothing to clean up:</strong> sessions close on their own when closing loses nothing: a shell at its prompt an hour after you left it (or at once when
              you close its terminal tab), a coding agent at rest a day after you last looked (when it can resume its own conversation), anything else after a week. Never one
              that's running, needs you, has output you haven't seen or serves a port. They wait in <em>Recently closed</em> for a week: reopening one brings back its folder,
              its output and the coding agent's conversation.
            </li>
            <li>
              When the program in a session exits with an error, the session stays until you've seen it: <em>Restart</em> runs it again, <em>Close</em> removes it. A program
              that ends cleanly leaves once nobody has it open.
            </li>
            <li>The window size follows whoever typed last, so a phone and a computer can share a session without fighting over its size.</li>
          </ul>
          <Table
            head={['Status', 'Means']}
            rows={[
              [<span className="guide-dot is-working">working</span>, 'The program printed output in the last few seconds (not just the echo of your typing).'],
              [<span className="guide-dot is-waiting">needs you</span>, 'The program asked for you: it rang the bell or sent a notification, as coding agents do when they wait for an answer. It clears when you type.'],
              [<span className="guide-dot is-done">done</span>, 'It printed something after you last looked: a finished task waiting to be read. It clears when you open it.'],
              [<span className="guide-dot is-idle">idle</span>, 'Quiet.'],
              [<span className="guide-dot is-exited">exited</span>, 'The program ended.'],
            ]}
          />
          <p>
            When a session in another tab or pane needs you, cmx shows a toast and, if you allow it, a browser notification. Click it to jump to the session. The browser asks for
            permission the first time.
          </p>
        </Section>

        <Section id="workspace" title="Workspace" lead="The browser workspace at /app: sessions on the left, up to four panes side by side.">
          <ul>
            <li>
              <strong>Layout:</strong> 1, 2 or 4 panes (the buttons at the top of the sidebar, or the palette). Collapse the sidebar for more room.
            </li>
            <li>
              <strong>Command palette</strong> ({mod}K): jump to a session, start one, open the desktop or a preview, change the layout.
            </li>
            <li>
              <strong>Status bar:</strong> the connection (WT is WebTransport, WS is WebSocket), the round trip to your cloud machine and how long keystrokes take to echo.
            </li>
          </ul>
          <Table
            head={['Keys', 'Does']}
            rows={[
              [<K>{mod}K</K>, 'Command palette'],
              [<K>{num}1 … 9</K>, 'Switch to session 1–9'],
              ...(isMac() ? ([[<K>⌘B</K>, 'Show or hide the sidebar']] as [ReactNode, ReactNode][]) : []),
              [<K>Esc</K>, 'Close the palette or menu'],
            ]}
          />
          <p className="muted">After an update to cmx, open tabs show “New version available” with a Reload button.</p>
        </Section>

        <Section id="files" title="Files and clipboard" lead="Drop or paste files into a session and they’re on your cloud machine, with their paths typed for you.">
          <ul>
            <li>
              <strong>Drop or paste files</strong> onto a session (web) or paste their paths into <C>cmx</C> (terminal): they upload to <C>~/.cache/cmx/drops/&lt;date&gt;/</C>{' '}
              and their paths on the cloud machine are pasted in their place. Up to 100 MB each; uploads are deleted after about 7 days.
            </li>
            <li>
              <strong>Screenshots and images:</strong> paste one and it also lands on the cloud machine’s clipboard, so apps that paste images (like Claude Code) get it. In the terminal,{' '}
              <K>Ctrl+V</K> first copies this computer’s clipboard to the cloud machine.
            </li>
            <li>
              <strong>Big or many files:</strong> <C>scp</C> and <C>rsync</C> after <C>cmx ssh-config</C>, e.g. <C>rsync -av ./site/ cmx:~/site/</C>.
            </li>
          </ul>
        </Section>

        <Section id="links" title="Links and previews" lead="Test on localhost as if your dev server ran on this computer, open it privately in the browser, or share it with a phone, a teammate or a webhook.">
          <ul>
            <li>
              <strong>localhost, automatically:</strong> while <C>cmx</C> is attached to a session, every port a program listens on on your cloud machine is also{' '}
              <C>localhost:&lt;port&gt;</C> on this computer. So OAuth callbacks, cookies and CORS written for localhost just work. A toast says so; if the port is taken
              on this computer it uses the next free one, says which, and stays there so the program here keeps its port. <C>cmx forward 3000</C> does it by hand; <C>cmx forward --off 3000</C> and <C>cmx forward --set 3000 --local 4000</C> decide for one port (<C>--on</C> goes back to the usual); <C>CMX_NO_AUTO_FORWARD=1</C> turns it all off.
            </li>
            <li>
              <strong>Previews:</strong> every listening port also shows under <em>previews</em> in the sidebar. Its link opens in a new tab and only works for you (signed
              in to cmx). From the terminal: <C>cmx open 3000</C>.
            </li>
            <li>
              <strong>Share:</strong> <em>Share</em> next to a preview (or <C>cmx share 3000</C>) makes a public link that anyone with it can open: your phone (scan the
              QR code), a teammate, or a webhook sender like Stripe or GitHub. It works for 1 hour, 1 day or 7 days, optionally with a password, and you can revoke it any
              time.
            </li>
            <li>
              <strong>Links programs open</strong> (like a sign-in page from <C>gh auth login</C>) open where they work, and the notice says where: “this computer’s
              browser” is the one you’re reading this in, “dev1’s browser” is the cloud machine’s (dev1 stands for its name).
              <ul>
                <li>A sign-in that calls back to a port on your cloud machine: with <C>cmx</C> attached, that port becomes localhost on this computer and the link opens in
                  this computer’s browser. With only the web open (or when the port is in use on this computer) it opens in the cloud machine’s own browser, and you finish on its{' '}
                  <a href="#desktop">desktop</a>: the web’s notice has Show desktop, and the CLI opens <C>cmx.sh/desktop</C> in a tab.</li>
                <li>A localhost link opens as its preview; anything else opens in this computer’s browser.</li>
                <li>The web shows a card (Open, Open in the cloud machine’s browser, Copy); the CLI opens it and <K>Enter</K> <K>~</K> <K>o</K> reopens the last link on this
                  computer, <K>Enter</K> <K>~</K> <K>m</K> sends it to the cloud machine’s browser.</li>
              </ul>
            </li>
          </ul>
        </Section>

        <Section id="desktop" title="Desktop" lead="Your cloud machine has a full Linux desktop with Chrome, streamed to the browser on this computer. Agents use it too: watch them work, or take over.">
          <ul>
            <li>
              <strong>Open:</strong> <em>Desktop</em> in the sidebar, the palette, <C>cmx desktop</C>, or <C>cmx.sh/desktop</C> in its own tab. It starts on demand and stops when you close it (<em>Stop</em> frees its CPU).
            </li>
            <li>
              <strong>Resolution:</strong> Auto fits the pane; or pick 720p to 1440p. Fullscreen fits your screen; a bar at the top edge has Exit and the resolution.
            </li>
            <li>
              <strong>One controller:</strong> the tab that opened it controls it; other tabs and devices watch (<em>View only</em>) and can <em>Take control</em>.
            </li>
            <li>
              <strong>Clipboard:</strong> allow it once (the Clipboard chip) and {mod}C / {mod}V work between this computer and the desktop’s apps; {isMac() ? '⌘ acts as Ctrl there.' : ''}
            </li>
            <li>
              <strong>Agents test in it:</strong> Claude Code and Codex on your cloud machine have a <em>browser</em> tool that drives this Chrome. Ask them to “open
              localhost:3000 and check the sign-up flow”: they click through, read console errors and take screenshots, and you can watch in the Desktop pane or take over.
            </li>
            <li>
              <strong>Streaming:</strong> WebRTC through cmx’s relay. On a network that blocks it (some offices and hotels) cmx switches to a compatibility mode and says so; it still
              works, with more lag. <em>Open in tab ↗</em> gives the desktop a whole tab.
            </li>
          </ul>
        </Section>

        <Section id="phone" title="Phone and tablet" lead="The workspace works on phones: sessions, previews and the desktop, with keys your on-screen keyboard lacks.">
          <ul>
            <li>
              <strong>Menu</strong> (☰): sessions, desktop, previews, dashboard, sign out.
            </li>
            <li>
              <strong>Key bar</strong> above the keyboard: esc, tab, ctrl, arrows, paste and hide. Tap ctrl, then a letter, for Ctrl+letter. It also drives the desktop.
            </li>
            <li>
              <strong>Select</strong> in the top bar selects text to copy; <strong>paste</strong> opens a box for longer text. Swipe to scroll; a tap in an app that takes the mouse
              clicks exactly where you tapped.
            </li>
            <li>
              On the desktop, <em>Copy</em> brings the cloud machine’s clipboard to your phone.
            </li>
          </ul>
        </Section>

        <Section id="cli" title="Command line" lead="cmx in your own terminal: its fonts, keys and scrollback, with the same sessions as the web.">
          <Cmd>{install}</Cmd>
          <p className="muted">
            Installs to <C>~/.local/bin</C> (or <C>/usr/local/bin</C> when writable) and signs you in. Each computer you sign in shows up on the <Link to="/dashboard">dashboard</Link>{' '}
            under <em>Signed-in devices</em>, where you can revoke it. cmx keeps itself up to date in the background; an attached cmx switches to the new version in place when you pause typing, in the same window.
          </p>
          {CLI_GROUPS.map((g) => (
            <div key={g.title} className="guide-ref" data-testid="guide-cli-group">
              <h3>{g.title}</h3>
              <dl>
                {g.commands.map((c) => (
                  <div key={c.name} className="guide-ref-item">
                    <dt>
                      <code>cmx {c.usage}</code>
                    </dt>
                    <dd>{c.what}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          <h3>While attached</h3>
          <p className="muted">
            Press <K>Enter</K> first, then:
          </p>
          <Table head={['Keys', 'Does']} rows={ESCAPE_KEYS.map(([k, v]) => [<K>{k}</K>, v] as [ReactNode, ReactNode])} />
          <h3>Environment</h3>
          <Table head={['Variable', 'Effect']} rows={CLI_ENV.map(([k, v]) => [<C>{k}</C>, v] as [ReactNode, ReactNode])} />
        </Section>

        <Section
          id="apps"
          title="Editors and AI apps"
          lead="Use the ChatGPT app (Codex), the Claude app (Claude Code) or VS Code on this computer, with the code, builds and agents on your cloud machine. Agents keep working while this computer sleeps; the apps reattach when it wakes."
        >
          <Cmd testid="guide-connect">cmx connect</Cmd>
          <p className="muted">It adds your cloud machine to ~/.ssh/config, sets up each app it finds, signs Codex in on your cloud machine if needed, and tells you where to click for the rest.</p>
          <Table
            head={['App', 'Set up']}
            rows={[
              [
                <AppName icon={IconBrandOpenai}>ChatGPT app (Codex)</AppName>,
                <>
                  <C>cmx connect</C> adds your cloud machine; quit and reopen ChatGPT and pick the project named after it. If it shows a warning: Settings → Connections →
                  SSH → switch <C>cmx-&lt;machine&gt;</C> on.
                </>,
              ],
              [
                <AppName icon={IconBrandClaude}>Claude app (Claude Code)</AppName>,
                <>
                  Code → environment menu → <em>+ Add SSH connection</em> → host <C>cmx-&lt;machine&gt;</C>, user and port empty. Claude Code is already installed on
                  your cloud machine.
                </>,
              ],
              [
                <AppName icon={IconBrandVscode}>VS Code</AppName>,
                <>
                  Install <em>Remote - SSH</em>, then <em>Remote-SSH: Connect to Host…</em> → <C>cmx</C>. <C>cmx connect</C> lets VS Code agents survive 7 days
                  disconnected instead of 3 hours (a VS Code setting for all your SSH hosts). Cursor works the same way.
                </>,
              ],
            ]}
          />
          <p>
            <strong>For long runs</strong>, let the agent work without asking: <em>Full access</em> in ChatGPT, <em>Bypass permissions</em> in Claude. An agent waiting for
            your approval waits until you’re back.
          </p>
          <p className="muted">
            <C>cmx ssh-config</C> alone sets up <C>ssh cmx</C>, <C>scp</C> and <C>rsync</C>. SSH only reaches your cloud machine through your signed-in cmx connection; nothing is
            exposed publicly.
          </p>
        </Section>

        <Section id="account" title="Account and security" lead="Your cloud machine is yours alone: every route to it goes through your cmx sign-in.">
          <ul>
            <li>
              <strong>Password:</strong> change it on the <Link to="/dashboard">dashboard</Link> under Account. <strong>Sign out</strong> from the dashboard or the sidebar.
            </li>
            <li>
              <strong>Signed-in devices</strong> (computers with the CLI, the apps) are listed on the dashboard; <em>Revoke</em> signs one out at once. <C>cmx logout</C> does it from that computer.
            </li>
            <li>Preview links, the desktop and SSH only work for you, signed in. Your cloud machine is backed up nightly.</li>
          </ul>
        </Section>

        {isAdmin && (
          <Section id="admin" title="Admin" lead="Admins make codes and look after cloud machines on the Admin page.">
            <ul>
              <li>
                <strong>Codes:</strong> up to 100% off a plan, for good or for some months, optionally for one size or a number of uses. Each has a sign-up link that fills it in;
                turn one off any time. <strong>Give founding plans</strong> puts everyone from before launch on a free plan at their size.
              </li>
              <li>
                <strong>Users, cloud machines and the waitlist</strong> are on the <Link to="/admin">Admin</Link> page.
              </li>
            </ul>
          </Section>
        )}

        <Section id="troubleshooting" title="Troubleshooting" lead="The usual suspects, and what to do.">
          <Table
            head={['You see', 'Try']}
            rows={[
              ['“Reconnecting to dev1…”', 'Your network dropped. cmx reconnects by itself, with nothing lost; the session kept running meanwhile.'],
              ['“cloud machine unreachable”', 'Your cloud machine is restarting or unreachable; wait a minute and reload. If it stays, write to hello@cmx.sh.'],
              ['“Nothing is listening on port N”', 'Start the dev server on your cloud machine (it must listen on the port), then reload.'],
              ['“preview link expired”', 'Open the preview again from the sidebar or with cmx open.'],
              ['Clipboard: Blocked', 'Allow clipboard access for cmx in this browser’s site settings, then reload.'],
              [<>command not found: cmx</>, <>Add the install folder to your PATH (the installer prints the line), or open a new terminal.</>],
              ['An agent stopped while this computer slept', 'It was probably waiting for your approval: use Full access / Bypass permissions for long runs.'],
              ['A toast says localhost:3000 is in use on this computer', 'Something on this computer already uses 3000, so the cloud machine’s 3000 is on the next free port (the toast names it).'],
              ['Desktop in compatibility mode', 'This network blocks the fast stream (UDP). It works, with more lag; another network brings the fast one back.'],
            ]}
          />
        </Section>
      </div>
    </div>
  );
}

function SignedOutChrome({ children }: { children: ReactNode }) {
  return (
    <div className="dash">
      <header className="topbar">
        <div className="topbar-inner">
          <Brand />
          <span className="spacer" />
          <Link to="/login?next=%2Fguide" className="btn btn-ghost">
            Sign in
          </Link>
        </div>
      </header>
      <main className="dash-main" id="main">
        <div className="dash-head">
          <h1>Guide</h1>
          <p className="muted">How to use cmx, your always-on cloud machine.</p>
        </div>
        {children}
      </main>
    </div>
  );
}

export function GuidePage() {
  const me = useStore((s) => s.me);
  const body = <GuideBody isAdmin={me?.role === 'admin'} />;
  if (!me) return <SignedOutChrome>{body}</SignedOutChrome>;
  return (
    <DashLayout title="Guide" subtitle="How to use cmx, your always-on cloud machine.">
      {body}
    </DashLayout>
  );
}
